import { FormOutlined, HomeOutlined, SettingOutlined } from '@ant-design/icons';
import DashboardDependencies from '@geosecma/dashboard/dist/dependencies.json';
import FileUploaderDependencies from '@geosecma/file-uploader/dist/dependencies.json';
import MapConfigDependencies from '@geosecma/map-configuration/dist/dependencies.json';
import MapDependencies from '@geosecma/map/dist/dependencies.json';
import SettingsDependencies from '@geosecma/settings/dist/dependencies.json';
import { AboutMenu, LinkMenu, Sidebar } from '@geosecma/sidebar';
import SidebarDependencies from '@geosecma/sidebar/dist/dependencies.json';
import { EditUserMenu } from 'components/EditUser/EditUserMenu';
import { ApplicationPaths } from 'lib/api-authorization/ApiAuthorizationConstants';
import authService from 'lib/api-authorization/AuthorizeService';
import { useRootStore } from 'models/rootModel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import diggingPermitLicenses from 'utils/diggingPermit.json';
import nugetLicenses from 'utils/packagesList.json';
import { VersionInfo } from './AboutDiggingPermit/VersionInfo';
import SelectMunicipality from './SelectMunicipality/SelectMunicipality';

const grcLicenses = [];
MapConfigDependencies.forEach(dependency => {
  if (!grcLicenses.find(grcLicense => grcLicense.name === dependency.name)) {
    grcLicenses.push({
      name: dependency.name,
      link: dependency.repository.url,
      licenseType: dependency.license,
      comment: dependency.version
    });
  }
});
MapDependencies.forEach(dependency => {
  if (!grcLicenses.find(grcLicense => grcLicense.name === dependency.name)) {
    grcLicenses.push({
      name: dependency.name,
      link: dependency.repository.url,
      licenseType: dependency.license,
      comment: dependency.version
    });
  }
});
FileUploaderDependencies.forEach(dependency => {
  if (!grcLicenses.find(grcLicense => grcLicense.name === dependency.name)) {
    grcLicenses.push({
      name: dependency.name,
      link: dependency.repository.url,
      licenseType: dependency.license,
      comment: dependency.version
    });
  }
});
SettingsDependencies.forEach(dependency => {
  if (!grcLicenses.find(grcLicense => grcLicense.name === dependency.name)) {
    grcLicenses.push({
      name: dependency.name,
      link: dependency.repository.url,
      licenseType: dependency.license,
      comment: dependency.version
    });
  }
});
DashboardDependencies.forEach(dependency => {
  if (!grcLicenses.find(grcLicense => grcLicense.name === dependency.name)) {
    grcLicenses.push({
      name: dependency.name,
      link: dependency.repository.url,
      licenseType: dependency.license,
      comment: dependency.version
    });
  }
});
SidebarDependencies.forEach(dependency => {
  if (!grcLicenses.find(grcLicense => grcLicense.name === dependency.name)) {
    grcLicenses.push({
      name: dependency.name,
      link: dependency.repository.url,
      licenseType: dependency.license,
      comment: dependency.version
    });
  }
});

export const CustomSidebar = () => {
  const [t] = useTranslation();
  const { municipality } = useRootStore();
  const [user, setUser] = useState();

  const getMunicipalityLinkPath = useCallback(
    linkPath => `/${municipality.tenantId}/${linkPath}`,
    [municipality.tenantId]
  );

  useEffect(() => {
    authService.getUser().then(setUser);
  }, []);

  const baseUrl = document.baseURI.endsWith('/') ? document.baseURI.slice(0, -1) : document.baseURI;
  const logoutUrl = `${baseUrl}${ApplicationPaths.LogOut}?returnUrl=${baseUrl}/`;

  const sidebarMenu = useMemo(
    () => ({
      title: t('application:title'),
      collapsedTitle: t('application:expand'),
      isCollapsed: true,
      localStorageKey: 'geosecma:application:sidebarCollapsed',
      firstMenu: [
        {
          hasAccess: true,
          props: {
            linkPath: getMunicipalityLinkPath('dashboard'),
            icon: <HomeOutlined />,
            title: t('application:dashboard'),
            key: 'dashboard'
          },
          menuComponent: LinkMenu
        },
        {
          hasAccess: true,
          props: {
            linkPath: getMunicipalityLinkPath('case'),
            icon: <FormOutlined />,
            title: t('application:createCase'),
            key: 'case'
          },
          menuComponent: LinkMenu
        }
      ],
      secondMenu: [
        {
          hasAccess: true,
          props: {
            key: 'selectMunicipality'
          },
          menuComponent: SelectMunicipality
        },
        {
          hasAccess: true,
          props: {
            key: 'user',
            logoutApi: logoutUrl
          },
          menuComponent: EditUserMenu
        },
        {
          hasAccess: user?.role === 'Admin',
          props: {
            linkPath: getMunicipalityLinkPath('settings'),
            key: 'settings',
            icon: <SettingOutlined />,
            title: t('application:settings')
          },
          menuComponent: LinkMenu
        },
        {
          hasAccess: true,
          props: {
            key: 'about',
            about: t('application:about'),
            title: t('general:licensesTitle'),
            jsLicenses: diggingPermitLicenses,
            nugetLicenses,
            grcLicenses,
            licensesListText: t('general:licensesList'),
            versionNumberComponent: <VersionInfo />,
            showSupportLinks: false
          },
          menuComponent: AboutMenu
        }
      ]
    }),
    [t, getMunicipalityLinkPath, user, logoutUrl]
  );

  return <Sidebar {...sidebarMenu} />;
};
